/**
 * Ready examples for the guided flow (ADR-0058): bilingual starting points over the wizard
 * starters. A template names tools and collections by reference; the UI drops the ones this
 * deployment does not have (and says so) when it applies a template.
 *
 * Every example must build and pass every check, without edits, against the local stack's
 * catalogue (`fixtures/liveCatalog.ts`, ADR-0087): only systems and actions it runs, a data
 * level at least that of the systems it uses, and a model allowed to see that level.
 */
import { planToCron, type SchedulePlan } from "../schedule";
import type { RegistryTool } from "../types";
import { chatModels, modelReach, type ModelFacts } from "./access";
import type { WizardState } from "./wizard";
import { emptyWizard, supportsAmount } from "./wizard";

export type WizardTemplate = Pick<WizardState, "starter" | "name" | "title" | "purpose" | "audience" | "languages" | "instructions"> &
  Partial<Pick<WizardState, "topics" | "collections" | "tools" | "approval" | "approverRoles" | "amountAbove" | "sensitivity" | "modelAlias" | "triggers">> & { id: string };

/** Every working day at 07:30, Riyadh time (the organisation's time zone replaces it when edited). */
const MORNING: SchedulePlan = { frequency: "weekdays", time: "07:30", workWeek: "sun-thu", weekday: 0, monthDay: 1, everyHours: 4 };
/** Sundays at 08:00, the first working day of the week (ADR-0091 weekly report). */
const WEEKLY: SchedulePlan = { frequency: "weekly", time: "08:00", workWeek: "sun-thu", weekday: 0, monthDay: 1, everyHours: 4 };

export const wizardTemplates: readonly WizardTemplate[] = [
  {
    id: "hr-policy-helper",
    starter: "answer_docs",
    name: "hr-policy-helper",
    title: { en: "HR policy helper", ar: "مساعد سياسات الموارد البشرية" },
    purpose: { en: "Answers staff questions about leave, travel and benefits from the HR policies", ar: "يجيب عن أسئلة الموظفين حول الإجازات والسفر والمزايا من سياسات الموارد البشرية" },
    audience: "All staff",
    languages: "both",
    instructions: "Quote the policy section you rely on. If the policy does not cover the question, say so.",
    collections: ["policies-kb"],
    sensitivity: "internal",
    modelAlias: "chat-default",
  },
  {
    id: "invoice-payments",
    starter: "approval_request",
    name: "invoice-payment-helper",
    title: { en: "Invoice payment helper", ar: "مساعد سداد الفواتير" },
    purpose: { en: "Looks up invoices and prepares payments for a finance approver to confirm", ar: "يبحث عن الفواتير ويجهّز المدفوعات ليعتمدها معتمِد مالي" },
    audience: "Accounts payable team",
    languages: "both",
    instructions: "Never schedule a payment for an invoice that is disputed.",
    tools: [{ ref: "erp", actions: ["read_invoice", "post_payment"] }],
    approval: "before_changes",
    approverRoles: ["finance-approver"],
    amountAbove: 10000,
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  {
    id: "it-access",
    starter: "approval_request",
    name: "it-access-requests",
    title: { en: "IT access requests", ar: "طلبات صلاحيات تقنية المعلومات" },
    purpose: { en: "Takes requests for system access and, once an approver agrees, hands them to the IT team's request queue", ar: "يستقبل طلبات الوصول إلى الأنظمة، وبعد موافقة المعتمِد يسلّمها إلى طابور طلبات فريق تقنية المعلومات" },
    audience: "All staff",
    languages: "both",
    instructions:
      "Ask who needs access, to which system, and the business reason. Then send one event to the destination \"requests\" with the event type \"access.requested\" and a payload with person, system, access and reason. Tell the person the IT team will follow up.",
    tools: [{ ref: "messagebus", actions: ["publish_event"] }],
    approval: "before_changes",
    approverRoles: ["approver"],
    sensitivity: "internal",
    modelAlias: "chat-default",
  },
  {
    id: "front-desk",
    starter: "route_requests",
    name: "front-desk-router",
    title: { en: "Front desk", ar: "مكتب الاستقبال" },
    purpose: { en: "Reads each request and sends it to HR, IT or Finance", ar: "يقرأ كل طلب ويحوّله إلى الموارد البشرية أو تقنية المعلومات أو المالية" },
    audience: "Employees and visitors",
    languages: "both",
    instructions: "",
    topics: [
      { en: "HR", ar: "الموارد البشرية" },
      { en: "IT", ar: "تقنية المعلومات" },
      { en: "Finance", ar: "المالية" },
    ],
    sensitivity: "internal",
    modelAlias: "chat-small",
  },
  {
    id: "supplier-form",
    starter: "fill_form",
    name: "supplier-form-filler",
    title: { en: "Supplier registration form", ar: "نموذج تسجيل المورّدين" },
    purpose: { en: "Fills the supplier registration form from the documents a supplier sends", ar: "يملأ نموذج تسجيل المورّد من المستندات التي يرسلها المورّد" },
    audience: "Procurement team",
    languages: "both",
    instructions: "Fields: company name, commercial registration number, VAT number, address, bank IBAN.",
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  {
    id: "customer-replies",
    starter: "draft_review",
    name: "customer-reply-writer",
    title: { en: "Customer reply writer", ar: "كاتب ردود العملاء" },
    purpose: { en: "Drafts polite, accurate replies to customer emails and checks them before they go out", ar: "يكتب ردودًا مهذبة ودقيقة على رسائل العملاء ويراجعها قبل إرسالها" },
    audience: "Customer service team",
    languages: "both",
    instructions: "Keep replies under 150 words.",
    approval: "always",
    approverRoles: ["reviewer"],
    sensitivity: "internal",
    modelAlias: "chat-default",
  },
  {
    id: "invoice-status",
    starter: "look_up",
    name: "invoice-status-lookup",
    title: { en: "Invoice status lookup", ar: "الاستعلام عن حالة الفواتير" },
    purpose: { en: "Looks up invoices in the finance system and reports their status, amount and due date", ar: "يبحث عن الفواتير في النظام المالي ويعرض حالتها ومبلغها وتاريخ استحقاقها" },
    audience: "Accounts payable team",
    languages: "both",
    instructions: "Invoice numbers look like INV-1043. Show a table with number, supplier, amount, status and due date.",
    tools: [{ ref: "erp", actions: ["read_invoice", "list_invoices"] }],
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  {
    id: "contract-review",
    starter: "check_document",
    name: "contract-checker",
    title: { en: "Contract checker", ar: "مدقق العقود" },
    purpose: { en: "Checks a supplier contract against the procurement policies and lists what is missing or risky", ar: "يراجع عقد المورّد مقابل سياسات المشتريات ويذكر الناقص أو الخطر" },
    audience: "Procurement and legal team",
    languages: "both",
    instructions: "Checklist: payment terms, liability cap, termination notice, data protection clause, governing law.",
    collections: ["policies-kb"],
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  {
    id: "overdue-digest",
    starter: "scheduled_report",
    name: "overdue-invoices-digest",
    title: { en: "Overdue invoices digest", ar: "ملخص الفواتير المتأخرة" },
    purpose: { en: "Every working morning, lists overdue invoices and e-mails the summary to the finance team", ar: "كل صباح عمل، يعرض الفواتير المتأخرة ويرسل الملخص بالبريد إلى فريق المالية" },
    audience: "Finance team",
    languages: "both",
    instructions: "List the overdue invoices, then send the summary by e-mail to finance-team@example.com. Put the oldest invoices first.",
    tools: [
      { ref: "erp", actions: ["list_invoices"] },
      { ref: "email", actions: ["send_email"] },
    ],
    sensitivity: "confidential",
    modelAlias: "chat-default",
    triggers: [{ type: "manual" }, { type: "cron", config: { schedule: planToCron(MORNING), timezone: "Asia/Riyadh", plan: { ...MORNING } } }],
  },
  {
    id: "complaint-helper",
    starter: "case_helper",
    name: "complaint-case-helper",
    title: { en: "Complaint case helper", ar: "مساعد قضايا الشكاوى" },
    purpose: { en: "Summarises a customer complaint, finds the related policy and suggests the next step to the case worker", ar: "يلخص شكوى العميل ويجد السياسة المتعلقة بها ويقترح الخطوة التالية على موظف القضية" },
    audience: "Customer care case workers",
    languages: "both",
    instructions: "Suggest one next step at a time: reply to the customer, ask for a document, or escalate to a supervisor.",
    collections: ["policies-kb"],
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  // The platform's work tools (ADR-0091): cases, notifications and documents.
  {
    id: "payment-failure-case",
    starter: "approval_request",
    name: "payment-failure-cases",
    title: { en: "Open a case when the payment fails", ar: "فتح قضية عند فشل الدفع" },
    purpose: { en: "Pays approved invoices; when a payment fails it opens a case for finance with the reason and tells the team", ar: "يسدّد الفواتير المعتمدة؛ وعند فشل الدفع يفتح قضية للمالية بالسبب ويُبلغ الفريق" },
    audience: "Accounts payable team",
    languages: "both",
    instructions:
      "Read the invoice, then post the payment. If the payment fails, do not say it was paid: open a case of the type \"payment_problem\" with the title \"Payment for <invoice> failed\", the field invoice_id set to the invoice number, the field reason set to what the payment system said, and the bank's answer as the description. Then send a notification to the role \"finance-approver\" with the template \"case_opened\" and the case title. Tell the person the case was opened only after the cases system confirmed it.",
    tools: [
      { ref: "erp", actions: ["read_invoice", "post_payment"] },
      { ref: "cases", actions: ["create_case"] },
      { ref: "notify", actions: ["send_notification"] },
    ],
    approval: "before_changes",
    approverRoles: ["finance-approver"],
    amountAbove: 10000,
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
  {
    id: "weekly-report-pdf",
    starter: "scheduled_report",
    name: "weekly-finance-report",
    title: { en: "Send the weekly report as a PDF to Finance", ar: "إرسال التقرير الأسبوعي إلى المالية بصيغة PDF" },
    purpose: { en: "Every Sunday, lists the overdue invoices, makes a PDF report and puts it in the finance team's notifications", ar: "كل يوم أحد، يعرض الفواتير المتأخرة ويُنشئ تقرير PDF ويضعه في إشعارات فريق المالية" },
    audience: "Finance team",
    languages: "both",
    instructions:
      "List the overdue invoices. Make a PDF with the template \"report\" (language \"en\", data_level \"confidential\"): title \"Overdue invoices this week\", a one-paragraph summary, rows with the invoice number and supplier as name and the amount as value, and the total. Then send a notification to the role \"finance\" with the template \"report_ready\", the PDF's name as name and the PDF's link as link.",
    tools: [
      { ref: "erp", actions: ["list_invoices"] },
      { ref: "documents", actions: ["make_pdf"] },
      { ref: "notify", actions: ["send_notification"] },
    ],
    // It works on its own each Sunday (G-camp-fin-26), so a finance approver checks what it sends.
    approval: "before_changes",
    approverRoles: ["finance-approver"],
    sensitivity: "confidential",
    modelAlias: "chat-default",
    triggers: [{ type: "manual" }, { type: "cron", config: { schedule: planToCron(WEEKLY), timezone: "Asia/Riyadh", plan: { ...WEEKLY } } }],
  },
  {
    id: "case-desk-helper",
    starter: "case_helper",
    name: "case-desk-helper",
    title: { en: "Case desk helper", ar: "مساعد مكتب القضايا" },
    purpose: { en: "Reads the case you are working on, finds the related policy and suggests the next step", ar: "يقرأ القضية التي تعمل عليها ويجد السياسة المتعلقة بها ويقترح الخطوة التالية" },
    audience: "Case workers",
    languages: "both",
    instructions: "Always read the case first: it is the case this conversation is about (in Try it, pick a case). Base your suggestion on its information and history, and name the policy section you rely on.",
    tools: [{ ref: "cases", actions: ["get_case"] }],
    collections: ["policies-kb"],
    sensitivity: "confidential",
    modelAlias: "chat-default",
  },
];

/**
 * A wizard state from a template, keeping only the tools, actions and collections this
 * deployment offers. `dropped` lists what was left out.
 */
export function templateState(
  tpl: WizardTemplate,
  tenant: string,
  owner: string,
  available: { tools?: readonly RegistryTool[]; collections?: readonly string[]; models?: readonly ModelFacts[] } = {},
): { state: WizardState; dropped: string[] } {
  const dropped: string[] = [];
  const tools = (tpl.tools ?? []).flatMap((t) => {
    if (!available.tools) return [t];
    const reg = available.tools.find((r) => r.ref === t.ref && r.certified);
    const actions = t.actions.filter((a) => reg?.actions.some((x) => x.name === a));
    t.actions.filter((a) => !actions.includes(a)).forEach((a) => dropped.push(`${t.ref}.${a}`));
    return actions.length ? [{ ref: t.ref, actions }] : [];
  });
  const collections = (tpl.collections ?? []).filter((c) => {
    const ok = !available.collections || available.collections.includes(c);
    if (!ok) dropped.push(c);
    return ok;
  });
  const base = emptyWizard(tenant, owner, tpl.starter);
  const sensitivity = tpl.sensitivity ?? base.sensitivity;
  const model = pickModel(tpl.modelAlias ?? "", sensitivity, available.models);
  const state: WizardState = {
    ...base,
    name: tpl.name,
    title: { ...tpl.title },
    purpose: { ...tpl.purpose },
    audience: tpl.audience,
    languages: tpl.languages,
    instructions: tpl.instructions,
    topics: tpl.topics ? tpl.topics.map((t) => ({ ...t })) : base.topics,
    collections,
    tools,
    approval: tpl.approval ?? base.approval,
    approverRoles: tpl.approverRoles ? [...tpl.approverRoles] : base.approverRoles,
    amountAbove: tpl.amountAbove ?? null,
    sensitivity,
    modelAlias: model,
    triggers: (tpl.triggers ?? base.triggers).map((t) => ({ ...t })),
  };
  // A threshold only where the chosen actions carry an amount (else it would ask every time).
  if (available.tools && state.amountAbove !== null && !supportsAmount(state, { tools: available.tools })) state.amountAbove = null;
  return { state, dropped };
}

/** The template's model when it is offered and may see the data; otherwise the first chat model
 * that may (all of its models first, then one that keeps the data on your own servers). A new
 * agent started from scratch gets the standard model the same way (G-camp-fin-22). */
export const DEFAULT_MODEL = "chat-default";
export function pickModel(wanted: string, level: WizardState["sensitivity"], models: readonly ModelFacts[] | undefined): string {
  if (!models) return wanted;
  const chat = chatModels(models);
  const ok = (m: ModelFacts) => modelReach(m, level) !== "no";
  const own = chat.find((m) => m.id === wanted);
  if (own && ok(own)) return own.id;
  return (chat.find((m) => modelReach(m, level) === "all") ?? chat.find(ok) ?? chat[0])?.id ?? "";
}
